import { useCallback, createContext, useState, useMemo } from 'react';

import { useFeatureValue } from '@growthbook/growthbook-react';
import * as Sentry from '@sentry/react';

import {
  AdvertisementEnv,
  getAdAPIUrl,
  getMultipleAdsAPIUrl,
} from '../../constants/urls';

import { AdvertisementFormData } from 'modules/advertisement/types';
import { AD_POSITIONS } from 'modules/advertisement/constants';

import { useAdProviderQueryLazyQuery } from 'common/apollo/graphql';
import { WebViewBridge } from 'common/webviewBridge';
import { env } from 'utils/env';

interface FetchAdDataParams {
  slot: string | string[];
  viewTemplate: string;
  token: string;
  mode: AdvertisementEnv;
}

interface AdServiceProviderReturn {
  data: AdvertisementFormData[];
  isLoading: boolean;
  error?: Error | null | undefined;
}

interface AdServiceProviderProps {
  children: React.ReactNode;
}

interface VideoPlayState {
  videoUrl: string;
  playSeconds?: number;
}

interface requestAdDataParams {
  adInfo: {
    slot: string | string[];
    viewTemplate: string;
  }[];
  token: string;
  mode?: AdvertisementEnv;
}

export interface AdServiceContextValue {
  requestAdData: (params: requestAdDataParams) => void;
  adState: AdServiceProviderReturn;
  viewedList: number[];
  videoStateList: VideoPlayState[];
  isAdViewed: (adId: number) => boolean;
  getVideoState: (videoUrl: string) => number;
  updateVideoStateList: (state: VideoPlayState) => void;
  postFeedAdPositions: number[];
}

export const AdServiceContext = createContext<AdServiceContextValue>({
  requestAdData: () => {},
  adState: {
    data: [],
    isLoading: false,
    error: null,
  },
  viewedList: [],
  videoStateList: [],
  isAdViewed: () => false,
  getVideoState: () => 0,
  updateVideoStateList: () => {},
  postFeedAdPositions: AD_POSITIONS,
});

export const AdService = () => {
  const AdServiceProvider = ({ children }: AdServiceProviderProps) => {
    const [adState, setAdState] = useState<AdServiceProviderReturn>({
      data: [],
      isLoading: false,
      error: null,
    });

    const [viewedList, setViewedList] = useState<number[]>([]);
    const [videoStateList, setVideoStateList] = useState<VideoPlayState[]>([]);

    const postFeedAdPositionsStr = useFeatureValue<string>(
      'ad-classroom-post-feed-positions',
      ''
    );
    let postFeedAdPositions = AD_POSITIONS;
    try {
      postFeedAdPositions = postFeedAdPositionsStr
        ? postFeedAdPositionsStr.split(',').map((pos) => parseInt(pos))
        : AD_POSITIONS;
    } catch (error) {
      Sentry.captureException(error);
    }

    const [getMe] = useAdProviderQueryLazyQuery({
      fetchPolicy: 'cache-first',
    });

    const fetchAdData = async ({
      slot,
      viewTemplate,
      token,
      mode,
    }: FetchAdDataParams) => {
      try {
        const resp = await getMe();
        const me = resp.data!.me!;

        let grades = [];
        let zipcodes = [];

        if (me.grade) {
          grades.push(me.grade?.toLowerCase());
        }

        if (me.schoolInfoDto) {
          grades.push(me.schoolInfoDto?.grade?.toLowerCase());

          if (me.schoolInfoDto?.school) {
            zipcodes.push(me.schoolInfoDto.school.zipcode);
          }
        }

        if (me.schoolInfoDtoForChildren) {
          for (const item of me.schoolInfoDtoForChildren) {
            grades.push(item?.grade?.toLowerCase());

            if (item?.school?.zipcode) {
              zipcodes.push(item.school.zipcode);
            }
          }
        }

        const body = {
          userId: me.id,
          slot,
          viewTemplate,
          properties: {
            language: me.language,
            role: me.role.toLowerCase(),
            countryCode: me.countryCode,
            channel: WebViewBridge.isClasstingRNWebView() ? 'mobile' : 'web',
            grades: Array.from(new Set(grades)),
            zipcodes: Array.from(new Set(zipcodes)),
          },
        };

        let adApiUrl = getMultipleAdsAPIUrl(mode);

        if (typeof slot === 'string' || slot instanceof String) {
          adApiUrl = getAdAPIUrl(mode);
        }

        setAdState((prev) => ({ ...prev, isLoading: true }));

        const response = await fetch(adApiUrl, {
          method: 'POST',
          headers: {
            authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        });

        if (response.status < 200 || response.status >= 400)
          throw new Error(`Server Error: ${response.status}`);

        const data = await response.json();

        /* #TODO: request 분리 필요
          아래 주석처럼 request 분리에 따라 Response의 타입 정의와 그에 따른 코드 분리를
          위한 리펙토링이 필요함
        */
        if (Object.prototype.toString.call(data) === '[object Array]') {
          setAdState((prev) => ({
            ...prev,
            data: [...prev.data, ...data],
          }));
        } else {
          setAdState((prev) => ({
            ...prev,
            data: [...prev.data, data],
          }));
        }
      } catch (error) {
        setAdState((prev) => ({ ...prev, data: [], error: error as Error }));
      } finally {
        setAdState((prev) => ({ ...prev, isLoading: false }));
      }
    };
    const isAdViewed = useCallback(
      (adId: number) => {
        const isViewed = viewedList.includes(adId);
        if (!isViewed) {
          setViewedList((prevList) => [...prevList, adId]);
        }
        return isViewed;
      },
      [viewedList]
    );
    const getVideoState = useCallback(
      (videoUrl: string) => {
        const returnVal = videoStateList.filter(
          (state) => state.videoUrl === videoUrl
        );
        return returnVal?.[0]?.playSeconds || 0;
      },
      [videoStateList]
    );

    const updateVideoStateList = useCallback(
      ({ videoUrl, playSeconds }: VideoPlayState) => {
        setVideoStateList((prevList) => {
          const filteredList = prevList.filter(
            (state) => state.videoUrl !== videoUrl
          );
          return [...filteredList, { videoUrl, playSeconds }];
        });
      },
      []
    );

    /* #TODO: request 분리 필요
       신규 광고 서버 변경에 따라 API 스펙이 변경됨(https://github.com/classtinginc/ad-service/wiki/%EC%8B%A0%EA%B7%9C-%EA%B4%91%EA%B3%A0-%EC%97%B0%EB%8F%99-Guide#api-%EC%8A%A4%ED%8E%99)
       가독성과 명확성을 위해 request를 분리하여 요청하도록 리펙토링 필요함
    */
    const requestAdData = useCallback(
      ({
        adInfo,
        token,
        mode = env.STAGE as AdvertisementEnv,
      }: requestAdDataParams) => {
        if (adInfo?.length === 0) return;

        setAdState((prev) => ({ ...prev, data: [] }));
        setViewedList([]);
        setVideoStateList([]);

        adInfo?.forEach(({ slot, viewTemplate }) => {
          fetchAdData({ slot, viewTemplate, token, mode });
        });
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      []
    );

    const value = useMemo(
      () => ({
        requestAdData,
        adState,
        viewedList,
        videoStateList,
        isAdViewed,
        getVideoState,
        updateVideoStateList,
        postFeedAdPositions,
      }),
      [
        requestAdData,
        adState,
        viewedList,
        videoStateList,
        isAdViewed,
        getVideoState,
        updateVideoStateList,
        postFeedAdPositions,
      ]
    );

    return (
      <AdServiceContext.Provider value={value}>
        {children}
      </AdServiceContext.Provider>
    );
  };

  return {
    AdServiceProvider,
    AdService,
  };
};
